import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  getCurrentContractor,
  getShowContactsModal,
  getUsers,
  getWorkOrders,
  getMsaSigningUrl,
  getShowMsaSigningModal,
  getProjects,
} from 'selectors/admin/contractors/contractors';
import {
  editContractor,
  loadContractorRequest,
  showContactsModal,
  showContactsRequest,
  showSignMsaModal,
  hideMsaSigningModal,
  clearContractorForm,
  setShowInviteUserContractorModal,
} from 'actions/admin/contractors/contractors';
import numeral from 'numeral';
import RouteTitle from 'components/shared/route-title/RouteTitle';
import DetailsHeader from 'components/shared/details/DetailsHeader';
import ContractorStatus from './ContractorStatus';
import DetailsInfo from 'components/shared/details/DetailsInfo';
import DetailsInfoItem from 'components/shared/details/DetailsInfoItem';
import './ContractorDetails.css';
import NavigationBar from 'components/shared/navigation/NavigationBar';
import Number from 'components/shared/text/Number';
import ContactsModal from 'components/shared/modal/ContactsModal';
import { formatPhoneNumber } from 'services/utils/text-util';
import { getCurrentUser } from 'selectors/authentication';
import { CONTRACTOR_ROLES, TYPES } from 'services/utils/users-util';
import { simpleScrollToTop } from 'services/utils/scroller-util';
import ContractorDetailsWorkOrders from 'components/admin/contractors/ContractorDetailsWorkOrders';
import ContractorDetailsInvoices from 'components/admin/contractors/ContractorDetailsInvoices';
import ContractorDetailsProjects from 'components/admin/contractors/ContractorDetailsProjects';
import ContractorDocumentsViewer from 'components/admin/contractors/ContractorDocumentsViewer';
import SigningModal from 'components/shared/modal/SigningModal';
import ContractorInviteUserModal from 'components/admin/contractors/ContractorInviteUserModal.js';
import { ROLES, usersUtil } from 'services/utils/users-util';
import Button from 'components/shared/button/Button';

class ContractorDetails extends Component {
  constructor(props) {
    super(props);
    // Keep track of id to avoid showing an "old" contractor while loading the current one
    this.contractorId = numeral(this.props.match.params.id).value();
    this.userIsSpackleAdmin = usersUtil.hasRole(
      this.props.currentUser,
      ROLES.SPACKLE_ADMIN
    );
    this.userIsAdmin = usersUtil.isAdmin(props.currentUser);
    this.state = {
      currentNav: this.userIsAdmin ? 1 : 3,
    };
  }

  componentDidMount() {
    this.props.dispatch(loadContractorRequest(this.contractorId));
  }

  handleContactsModalClose = () => {
    this.props.dispatch(showContactsModal(false));
  };

  handleEdit = () => {
    simpleScrollToTop();
    this.props.dispatch(editContractor(this.props.currentContractor.id));
  };

  handleNavSelect = (value) => {
    this.setState({
      currentNav: value,
    });
  };

  handleViewAll = (event) => {
    event.preventDefault();
    this.props.dispatch(
      showContactsRequest(this.props.currentContractor.id, this.props.users)
    );
  };

  mapTitle(roles) {
    const titles = roles
      .filter((role) => role.resource_type === TYPES.CONTRACTOR)
      .map((role) => {
        return CONTRACTOR_ROLES[role.name];
      });

    if (titles.length > 1) {
      return titles.join(' / ');
    } else if (titles.length === 1) {
      return titles[0];
    } else {
      return 'N/A';
    }
  }

  mapUsers = (users) => {
    return users.map((user) => {
      return {
        email: user.email,
        lastSignIn: user.current_sign_in_at,
        name: `${user.first_name} ${user.last_name}`,
        phoneNumber: user.phone_number,
        primaryContact: user.account_creator,
        title: this.mapTitle(user.roles),
      };
    });
  };

  handleSignMsa = () => {
    this.props.dispatch(showSignMsaModal(this.props.currentContractor.id));
  };

  handleMsaSigningModalClose = () => {
    this.props.dispatch(hideMsaSigningModal());
  };

  handleInviteUserClick = () => {
    this.props.dispatch(clearContractorForm());
    this.props.dispatch(setShowInviteUserContractorModal(true));
  };

  render() {
    const { currentContractor } = this.props;

    let firstRowDetailsInfoItems = [];
    let secondRowDetailsInfoItems = [];

    if (currentContractor) {
      if (currentContractor.location) {
        firstRowDetailsInfoItems.push(
          <DetailsInfoItem
            lines={[
              currentContractor.location.street_address_1,
              currentContractor.location.city +
                ', ' +
                currentContractor.location.state +
                ' ' +
                currentContractor.location.zip,
            ]}
            title="ADDRESS"
          />
        );
      }
      firstRowDetailsInfoItems.push(
        <DetailsInfoItem
          lines={[
            <span className="contractor-name">
              {`${currentContractor.contact.first_name} ${currentContractor.contact.last_name}`}
            </span>,
            <a
              className="contractor-email"
              href={`mailto:${currentContractor.contact.email}`}
            >
              {currentContractor.contact.email}
            </a>,
            formatPhoneNumber(currentContractor.contact.phone_number),
          ]}
          title={
            <div>
              <span>PRIMARY CONTACT</span>
              <span className="view-all" onClick={this.handleViewAll}>
                View All
              </span>
            </div>
          }
        />
      );
      firstRowDetailsInfoItems.push(
        <DetailsInfoItem
          lines={[currentContractor.license_number]}
          title="LICENSE NUMBER"
        />
      );
      secondRowDetailsInfoItems.push(
        <DetailsInfoItem
          lines={currentContractor.service_areas.map((element) => {
            return element.name;
          })}
          title="AREAS OF SERVICE"
        />
      );
      secondRowDetailsInfoItems.push(
        <DetailsInfoItem
          lines={currentContractor.unions.map((element) => {
            return (
              <span>
                {element.label}
                {element.detail !== null && (
                  <span className="union-detail">({element.detail})</span>
                )}
              </span>
            );
          })}
          title="UNIONS"
        />
      );
      secondRowDetailsInfoItems.push(
        <DetailsInfoItem
          lines={currentContractor.trades.map((element) => {
            return element.label;
          })}
          title="SPECIALITIES"
          truncate={true}
        />
      );
    }

    let rightHeaders = [];
    if (currentContractor) {
      rightHeaders.push({
        title: 'STATUS',
        value: <ContractorStatus status={currentContractor.status} />,
      });

      if (this.userIsAdmin) {
        rightHeaders.push(
          {
            title: 'IN PROGRESS',
            value: currentContractor.wo_in_progress_count,
          },
          {
            title: 'COMPLETED',
            value: currentContractor.wo_complete_count,
          },
          {
            title: 'TOTAL EARNED',
            value:
              currentContractor.total_earned !== null ? (
                <Number value={currentContractor.total_earned} format="$0,0" />
              ) : (
                'N/A'
              ),
          }
        );
      }
    }

    return (
      <div className="contractor-details">
        {currentContractor !== null &&
          currentContractor.id === this.contractorId && (
            <React.Fragment>
              <RouteTitle
                titles={['CONTRACTORS', currentContractor.name.toUpperCase()]}
              />

              <DetailsHeader
                leftHeader={{
                  title: 'CONTRACTOR',
                  value: currentContractor.name,
                }}
                rightHeaders={[rightHeaders]}
              />

              <div className="details-info-container">
                <DetailsInfo
                  itemsContainerSize={10}
                  itemsLines={[
                    firstRowDetailsInfoItems,
                    secondRowDetailsInfoItems,
                  ]}
                  rightContainerSize={2}
                  rightContainer={
                    <div className="contractors-documents-container">
                      <ContractorDocumentsViewer
                        contractor={currentContractor}
                      />
                    </div>
                  }
                />

                <div className="edit-button-container">
                  {this.userIsAdmin &&
                    currentContractor.confirmed &&
                    !currentContractor.msa_document && (
                      <input
                        type="button"
                        className="btn button button-orange"
                        value="Sign MSA"
                        onClick={this.handleSignMsa}
                      />
                    )}
                  <input
                    type="button"
                    className="btn button button-gray"
                    value="Edit"
                    onClick={this.handleEdit}
                  />
                </div>
              </div>

              <div className="content-container">
                <React.Fragment>
                  <NavigationBar
                    items={[
                      {
                        id: 1,
                        title: 'Work Orders',
                        show: this.userIsAdmin,
                      },
                      {
                        id: 2,
                        title: 'Invoices',
                        show: this.userIsAdmin,
                      },
                      {
                        id: 3,
                        title: 'Projects',
                        class: this.state.currentNav === 3 ? 'selected' : '',
                      },
                    ]}
                    onSelected={this.handleNavSelect}
                  />

                  <div className="separator">
                    <div className="clearfix" />
                  </div>

                  <div className="navigation-content">
                    {this.state.currentNav === 1 && (
                      <ContractorDetailsWorkOrders />
                    )}
                    {this.state.currentNav === 2 && (
                      <ContractorDetailsInvoices />
                    )}
                    {this.state.currentNav === 3 && (
                      <ContractorDetailsProjects />
                    )}
                  </div>
                </React.Fragment>
              </div>
            </React.Fragment>
          )}

        <ContactsModal
          footer={
            this.userIsSpackleAdmin ? (
              <div className="contacts-button-container">
                <Button
                  label="Invite User"
                  className="button-orange"
                  onClick={this.handleInviteUserClick}
                />
              </div>
            ) : null
          }
          show={this.props.showContactsModal}
          users={this.mapUsers(this.props.users)}
          onClose={this.handleContactsModalClose}
        />

        <SigningModal
          show={this.props.showMsaSigningModal}
          url={this.props.msaSigningUrl}
          title="Sign MSA"
          handleClose={this.handleMsaSigningModalClose}
        />

        <ContractorInviteUserModal />
      </div>
    );
  }
}

export default connect((state) => {
  return {
    currentContractor: getCurrentContractor(state),
    currentUser: getCurrentUser(state),
    showContactsModal: getShowContactsModal(state),
    users: getUsers(state),
    workOrders: getWorkOrders(state),
    msaSigningUrl: getMsaSigningUrl(state),
    showMsaSigningModal: getShowMsaSigningModal(state),
    projects: getProjects(state),
  };
})(ContractorDetails);



// WEBPACK FOOTER //
// ./src/components/admin/contractors/ContractorDetails.js